import React, { createContext, useState, ReactNode, useMemo, useEffect, useCallback } from 'react';
import { ThemeProvider, Theme, GlobalStyles } from '@mui/material';
import { createInspirusLight } from './inspirus';
import { createInspirusDark } from './inspirusDark';
import { licenseCardGlobalStyles } from './licenseCardStyles';
import { emailTemplateEditorStyles } from './emailTemplateEditorStyles';
import { useAppSelector } from '../redux/store';

export type ThemeMode = 'light' | 'dark' | 'system';

interface ThemeContextType {
	toggleTheme: () => void;
	setThemeMode: (mode: ThemeMode) => void;
	themeMode: ThemeMode;
	currentTheme: Theme;
	isDark: boolean;
}

export const CustomThemeContext = createContext<ThemeContextType>({
	// eslint-disable-next-line @typescript-eslint/no-empty-function
	toggleTheme: () => {},
	// eslint-disable-next-line @typescript-eslint/no-empty-function
	setThemeMode: () => {},
	themeMode: 'system',
	currentTheme: createInspirusLight(),
	isDark: false
});

const getSystemDark = () => window.matchMedia('(prefers-color-scheme: dark)').matches;

export const CustomThemeProvider = ({ children }: { children: ReactNode }) => {
	const fontFamily = useAppSelector((state) => state.platformSettings.fontFamily);
	const fontWeight = useAppSelector((state) => state.platformSettings.fontWeight);

	const [themeMode, setThemeModeState] = useState<ThemeMode>(() => {
		const stored = localStorage.getItem('theme') as ThemeMode | null;
		if (stored === 'light' || stored === 'dark' || stored === 'system') return stored;
		// Legacy: if stored was set by old toggle but not 'system', keep it
		if (stored) return stored === 'dark' ? 'dark' : 'light';
		return 'system';
	});

	const [systemDark, setSystemDark] = useState(getSystemDark);

	const isDark = themeMode === 'system' ? systemDark : themeMode === 'dark';

	const setThemeMode = useCallback((mode: ThemeMode) => {
		setThemeModeState(mode);
		localStorage.setItem('theme', mode);
	}, []);

	// Legacy toggle: cycles light → dark → system → light
	const toggleTheme = useCallback(() => {
		setThemeModeState((prev) => {
			const next = prev === 'light' ? 'dark' : prev === 'dark' ? 'system' : 'light';
			localStorage.setItem('theme', next);
			return next;
		});
	}, []);

	const currentTheme = useMemo(
		() => (isDark ? createInspirusDark(fontFamily, fontWeight) : createInspirusLight(fontFamily, fontWeight)),
		[isDark, fontFamily, fontWeight]
	);

	// Sync if localStorage changes externally
	useEffect(() => {
		const handleStorage = (event: StorageEvent) => {
			if (event.key === 'theme') {
				const val = event.newValue as ThemeMode | null;
				if (val === 'light' || val === 'dark' || val === 'system') {
					setThemeModeState(val);
				}
			}
		};
		window.addEventListener('storage', handleStorage);
		return () => window.removeEventListener('storage', handleStorage);
	}, []);

	// Follow system preference changes
	useEffect(() => {
		const mediaQuery = window.matchMedia('(prefers-color-scheme: dark)');
		const handleChange = (e: MediaQueryListEvent) => {
			setSystemDark(e.matches);
		};
		mediaQuery.addEventListener('change', handleChange);

		// Chrome doesn't always fire the change event on macOS when system
		// appearance changes while the tab is backgrounded. Re-check on focus.
		const handleVisibility = () => {
			if (document.visibilityState === 'visible') {
				setSystemDark(getSystemDark());
			}
		};
		document.addEventListener('visibilitychange', handleVisibility);

		return () => {
			mediaQuery.removeEventListener('change', handleChange);
			document.removeEventListener('visibilitychange', handleVisibility);
		};
	}, []);

	// Sync data-theme attribute on <html> for global CSS targeting
	useEffect(() => {
		document.documentElement.setAttribute('data-theme', isDark ? 'dark' : 'light');
	}, [isDark]);

	return (
		<CustomThemeContext.Provider value={{ toggleTheme, setThemeMode, themeMode, currentTheme, isDark }}>
			<ThemeProvider theme={currentTheme}>
				{/* Global CSS at-rules that theme `styleOverrides` cannot express. Renders no markup. */}
				<GlobalStyles styles={licenseCardGlobalStyles} />
				{/* Class-scoped styles for the email template editor (PTEC-895); follow the active palette. */}
				<GlobalStyles styles={(theme: Theme) => emailTemplateEditorStyles(theme)} />
				{children}
			</ThemeProvider>
		</CustomThemeContext.Provider>
	);
};
